import React from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { RoleChoice, RoomMember } from '@/redux/types/room'
import { cn } from '@/lib/utils'
import { ActivityState } from '@/redux/slices/timerConfigurationSlice'

interface TeamCommunicatorSelectorProps {
  roundNumber: number
  roundStatus: string
  teamMembers: RoomMember[]
  currentCommunicator: RoomMember | null
  onChange: (communicatorId: number | null) => void
  isRequired?: boolean
  isReadOnly?: boolean
  className?: string
}

export const TeamCommunicatorSelector: React.FC<
  TeamCommunicatorSelectorProps
> = ({
  roundNumber,
  roundStatus,
  teamMembers,
  currentCommunicator,
  onChange,
  isRequired = false,
  isReadOnly = false,
  className,
}) => {
  const isRoundStarted = roundStatus !== ActivityState.NotStarted
  const isDisabled = isReadOnly || isRoundStarted

  const handleValueChange = (value: string) => {
    if (value === 'none') {
      onChange(null)
    } else {
      onChange(parseInt(value, 10))
    }
  }

  const currentValue = currentCommunicator
    ? (currentCommunicator as RoomMember & { id?: number }).id?.toString()
    : isRequired
      ? undefined
      : 'none'

  const playerMembers = teamMembers.filter(
    (member) => member.role === RoleChoice.PLAYER
  )

  const placeholderText =
    isRequired && !currentCommunicator
      ? 'Required - Select communicator'
      : isRoundStarted
        ? 'Cannot change (round started)'
        : 'Select communicator'

  return (
    <div className={cn('w-full', className)}>
      <Select
        value={currentValue}
        onValueChange={handleValueChange}
        disabled={isDisabled}
      >
        <SelectTrigger
          className={cn(
            'w-full px-4 py-3 min-h-[3rem]',
            isRequired && !currentCommunicator && 'border-red-300',
            isDisabled && 'opacity-60 cursor-not-allowed'
          )}
        >
          <SelectValue placeholder={placeholderText} />
        </SelectTrigger>
        <SelectContent>
          {!isRequired && (
            <SelectItem value='none'>
              <span className='text-muted-foreground'>None</span>
            </SelectItem>
          )}
          {playerMembers.length === 0 ? (
            <SelectItem value='no-members' disabled>
              <span className='text-muted-foreground'>No players in team</span>
            </SelectItem>
          ) : (
            playerMembers
              .map((member) => {
                const roomMemberId = (member as RoomMember & { id?: number }).id
                if (!roomMemberId) {
                  return null
                }
                return (
                  <SelectItem
                    key={roomMemberId}
                    value={roomMemberId.toString()}
                  >
                    <div className='flex flex-col'>
                      <span className='font-medium'>
                        {member.member_name || member.member_email}
                      </span>
                      {member.member_email && member.member_name && (
                        <span className='text-xs text-muted-foreground'>
                          {member.member_email}
                        </span>
                      )}
                    </div>
                  </SelectItem>
                )
              })
              .filter(Boolean)
          )}
        </SelectContent>
      </Select>
      {isRequired && !currentCommunicator && (
        <p className='mt-1 text-xs text-red-500'>
          Round {roundNumber} communicator is required
        </p>
      )}
    </div>
  )
}
